/**
 * OnboardingIntroPage — cinematic welcome experience.
 *
 * Single left-aligned, dark, deep-zoom experience that walks new subscribers
 * through five flagship modules. Timeline is driven by an <audio> element:
 *
 *   1. If /onboarding-voiceover.mp3 exists (baked once via Kaharian TTS), we
 *      stream it. Zero credit spend for every subscriber, forever.
 *   2. Otherwise the browser's SpeechSynthesis API narrates the same script.
 *      Still free, still zero credits, purely client-side.
 *
 * Visuals:
 *   - Deep parallax + subtle grain (no gradients over dark)
 *   - Chapter zoom-ins with soft blur ← reveal
 *   - Progress rail + skip / mute / replay controls
 *
 * When the last chapter ends we route to `/onboarding` (RoleAwareOnboardingEntry)
 * which loads the actual setup wizard.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  SkipForward, Volume2, VolumeX, RotateCcw, Rocket, Video, Wand2,
  Package as PackageIcon, Clapperboard, Calendar as CalIcon, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Visual metadata per chapter (order MUST match backend chapter ids).
const SCENE = {
  welcome: {
    label: 'Welcome',
    accent: '#22d3ee', // cyan
    Icon: Sparkles,
    render: (progress) => <WelcomeScene progress={progress} />,
  },
  konnectmeet: {
    label: 'KonnectMeet',
    accent: '#a78bfa', // violet
    Icon: Video,
    render: (progress) => <MeetScene progress={progress} />,
  },
  powerprompt: {
    label: 'PowerPrompt',
    accent: '#f472b6', // pink
    Icon: Wand2,
    render: (progress) => <PromptScene progress={progress} />,
  },
  vending: {
    label: 'Vending Fleet',
    accent: '#facc15', // amber
    Icon: PackageIcon,
    render: (progress) => <VendingScene progress={progress} />,
  },
  koolkonnect: {
    label: 'KoolKonnect',
    accent: '#34d399', // emerald
    Icon: Clapperboard,
    render: (progress) => <KoolKonnectScene progress={progress} />,
  },
  booking: {
    label: 'Booking',
    accent: '#60a5fa', // blue
    Icon: CalIcon,
    render: (progress) => <BookingScene progress={progress} />,
  },
  outro: {
    label: "Let's go",
    accent: '#f87171', // coral
    Icon: Rocket,
    render: (progress) => <OutroScene progress={progress} />,
  },
};

const CHAPTER_ORDER = ['welcome', 'konnectmeet', 'powerprompt', 'vending', 'koolkonnect', 'booking', 'outro'];

export default function OnboardingIntroPage({ mode = 'subscriber' }) {
  const isPublic = mode === 'public';
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const speechRef = useRef(null);
  const [chapters, setChapters] = useState([]);
  const [hasMp3, setHasMp3] = useState(false);
  const [t, setT] = useState(0); // current time in seconds
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);

  // Load script & availability — status is now public so this works signed-out too.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [scriptR, statusR] = await Promise.allSettled([
          axios.get(`${API}/api/onboarding-intro/script`),
          axios.get(`${API}/api/onboarding-intro/status`),
        ]);
        if (cancelled) return;
        if (scriptR.status === 'fulfilled') {
          setChapters(scriptR.value.data.chapters || []);
          setDuration(scriptR.value.data.duration_seconds_estimate || 55);
        }
        if (statusR.status === 'fulfilled') {
          setHasMp3(!!statusR.value.data.has_mp3);
        }
      } catch (_e) {
        // Silent — falls back to hardcoded defaults + browser TTS.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Which chapter contains time `t`
  const currentChapter = useMemo(() => {
    if (!chapters.length) return null;
    for (let i = 0; i < chapters.length; i++) {
      if (t >= chapters[i].start && t < chapters[i].end) return { ...chapters[i], idx: i };
    }
    return { ...chapters[chapters.length - 1], idx: chapters.length - 1 };
  }, [t, chapters]);

  // Progress inside the current chapter for zoom-in easing
  const localProgress = useMemo(() => {
    if (!currentChapter) return 0;
    const span = Math.max(0.01, currentChapter.end - currentChapter.start);
    return Math.min(1, Math.max(0, (t - currentChapter.start) / span));
  }, [t, currentChapter]);

  // Start playback (must be triggered by user gesture on many browsers).
  const start = async () => {
    setStarted(true);
    if (hasMp3 && audioRef.current) {
      try {
        audioRef.current.muted = muted;
        await audioRef.current.play();
      } catch (_e) {
        // Autoplay blocked — user can hit start again.
      }
    } else {
      // Fallback: browser SpeechSynthesis. Zero credits.
      speakChaptersFallback();
    }
  };

  const speakChaptersFallback = () => {
    if (!('speechSynthesis' in window) || !chapters.length) {
      // No TTS at all → just run visual timer.
      runVisualTimer();
      return;
    }
    try { window.speechSynthesis.cancel(); } catch (_e) { /* ignore */ }
    const voices = window.speechSynthesis.getVoices();
    // Prefer a confident English voice. Fallback = default.
    const voice = voices.find(v => /en-(US|GB)/i.test(v.lang) && /(daniel|onyx|male|google)/i.test(v.name))
               || voices.find(v => /en-(US|GB)/i.test(v.lang))
               || voices[0];
    let elapsed = 0;
    chapters.forEach((c, i) => {
      const u = new SpeechSynthesisUtterance(c.text);
      if (voice) u.voice = voice;
      u.rate = 1.02;
      u.pitch = 0.98;
      u.volume = muted ? 0 : 1;
      u.onstart = () => setT(elapsed + 0.05);
      u.onend = () => {
        elapsed = c.end;
        setT(elapsed);
        if (i === chapters.length - 1) finalize();
      };
      // Store reference for cancel on unmount.
      speechRef.current = u;
      window.speechSynthesis.speak(u);
    });
    runVisualTimer();
  };

  const runVisualTimer = () => {
    // Independent visual driver in case speech events lag.
    const started = performance.now();
    const tick = () => {
      const secs = (performance.now() - started) / 1000;
      setT(secs);
      if (secs >= (chapters.length ? chapters[chapters.length - 1].end : duration)) {
        finalize();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const finalize = () => {
    setEnded(true);
    try { window.speechSynthesis.cancel(); } catch (_e) { /* ignore */ }
    if (!isPublic) {
      localStorage.setItem('konnect_intro_seen_v1', '1');
      setTimeout(() => navigate('/onboarding'), 1200);
    }
    // Public mode: stay on the end overlay with the "Start free trial" CTA.
  };

  const skip = () => {
    try { window.speechSynthesis.cancel(); } catch (_e) { /* ignore */ }
    if (audioRef.current) { audioRef.current.pause(); }
    finalize();
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      if (audioRef.current) audioRef.current.muted = next;
      if (window.speechSynthesis) {
        // Adjust upcoming utterances only — current cannot change volume mid-flight.
      }
      return next;
    });
  };

  const replay = () => {
    try { window.speechSynthesis.cancel(); } catch (_e) { /* ignore */ }
    if (audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(() => {}); }
    setEnded(false);
    setT(0);
    if (!hasMp3) speakChaptersFallback();
  };

  // Cleanup speech synthesis when the page unmounts to prevent stranded utterances
  useEffect(() => {
    return () => {
      try { window.speechSynthesis.cancel(); } catch (_e) { /* ignore */ }
    };
  }, []);

  // SEO + social — public share pages need a proper title.
  useEffect(() => {
    if (!isPublic) return;
    const prev = document.title;
    document.title = 'Meet Konnect — the operating system for your entire business';
    return () => { document.title = prev; };
  }, [isPublic]);

  const sceneKey = currentChapter?.id || CHAPTER_ORDER[0];
  const scene = SCENE[sceneKey] || SCENE.welcome;

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden bg-[#050510] text-white"
      data-testid="onboarding-intro"
      style={{ fontFamily: '"General Sans", "Inter", ui-sans-serif, system-ui' }}
    >
      {/* Deep parallax layers */}
      <div className="absolute inset-0 opacity-70 pointer-events-none"
           style={{
             backgroundImage: `radial-gradient(1200px 500px at 20% 15%, ${scene.accent}22, transparent 60%),
                               radial-gradient(900px 500px at 80% 85%, ${scene.accent}18, transparent 60%)`,
             transition: 'background-image 1400ms cubic-bezier(.16,.84,.44,1)',
           }} />
      {/* Grain */}
      <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
           style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'160\' height=\'160\'><filter id=\'n\'><feTurbulence baseFrequency=\'0.9\' numOctaves=\'2\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\' opacity=\'0.8\'/></svg>")' }} />

      {/* Scene stage */}
      <div className="relative h-full w-full flex flex-col">
        {/* Top rail */}
        <div className="relative flex items-center justify-between px-8 py-6 z-20">
          <div className="flex items-center gap-3">
            <img src="/konnect-logo-white.png" alt="Konnect" className="h-7 w-auto opacity-90" />
            <span className="text-xs uppercase tracking-[0.3em] text-white/40">operating system</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMute}
              className="p-2 rounded-full border border-white/10 hover:bg-white/5 transition"
              aria-label={muted ? 'Unmute' : 'Mute'}
              data-testid="intro-mute-btn"
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <button
              onClick={replay}
              className="p-2 rounded-full border border-white/10 hover:bg-white/5 transition"
              aria-label="Replay"
              data-testid="intro-replay-btn"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
            <button
              onClick={skip}
              className="ml-2 px-4 py-2 rounded-full border border-white/10 hover:bg-white/5 text-sm flex items-center gap-2 transition"
              data-testid="intro-skip-btn"
            >
              Skip <SkipForward className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Center stage — the actual zoom-in scenes */}
        <div className="relative flex-1 flex items-center justify-center px-8 z-10">
          {!started && !ended && <StartOverlay onStart={start} hasMp3={hasMp3} isPublic={isPublic} />}
          {started && !ended && (
            <SceneStage
              key={sceneKey}
              chapter={currentChapter}
              scene={scene}
              localProgress={localProgress}
            />
          )}
          {ended && <EndOverlay isPublic={isPublic} onReplay={replay} />}
        </div>

        {/* Chapter caption + rail */}
        <div className="relative px-8 pb-10 z-20">
          <div className="max-w-4xl">
            <div className="text-[10px] uppercase tracking-[0.35em] text-white/40 mb-2">
              Chapter {(currentChapter?.idx ?? 0) + 1} of {chapters.length || 7} · {scene.label}
            </div>
            {started && currentChapter && (
              <p
                className="text-2xl sm:text-3xl leading-snug font-light text-white/90"
                style={{
                  opacity: localProgress < 0.05 ? 0 : Math.min(1, localProgress * 3),
                  transform: `translateY(${(1 - Math.min(1, localProgress * 3)) * 8}px)`,
                  transition: 'opacity 500ms, transform 700ms',
                }}
                data-testid={`intro-caption-${currentChapter.id}`}
              >
                {currentChapter.text}
              </p>
            )}
          </div>

          {/* Progress rail */}
          <div className="mt-6 h-[3px] w-full bg-white/5 rounded-full overflow-hidden">
            <div className="flex h-full">
              {chapters.map((c, i) => {
                const active = t >= c.start;
                const local = t >= c.start && t < c.end
                  ? (t - c.start) / Math.max(0.01, c.end - c.start)
                  : (t >= c.end ? 1 : 0);
                const span = Math.max(0.001, c.end - c.start);
                const totalSpan = chapters[chapters.length - 1].end;
                return (
                  <div key={c.id} style={{ width: `${(span / totalSpan) * 100}%` }} className="relative h-full">
                    <div
                      className="h-full transition-[width] duration-200"
                      style={{
                        width: `${local * 100}%`,
                        background: SCENE[c.id]?.accent || '#22d3ee',
                        opacity: active ? 1 : 0.4,
                      }}
                    />
                    {i < chapters.length - 1 && <div className="absolute right-0 top-0 h-full w-px bg-black/40" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden audio for baked MP3 */}
      {hasMp3 && (
        <audio
          ref={audioRef}
          src="/onboarding-voiceover.mp3"
          preload="auto"
          onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || duration)}
          onEnded={finalize}
        />
      )}
    </div>
  );
}

// ─── overlays ────────────────────────────────────────────────────────────────
function StartOverlay({ onStart, hasMp3, isPublic }) {
  return (
    <div className="text-center max-w-xl" data-testid="intro-start-overlay">
      <div className="text-xs uppercase tracking-[0.4em] text-white/40 mb-6">
        A guided welcome, ~55 seconds
      </div>
      <h1 className="text-5xl sm:text-7xl font-light leading-[1.02] mb-4">
        Meet Konnect.
      </h1>
      <p className="text-white/60 mb-10 text-base">
        {isPublic
          ? 'Five modules, one operating system for your entire business. See how it all fits together in under a minute.'
          : 'Five modules, one operating system for your entire business.'}
        {hasMp3 ? ' Narrated for you.' : ' Narrated by your browser.'}
      </p>
      <button
        onClick={onStart}
        className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-black hover:bg-white/90 transition font-medium"
        data-testid="intro-start-btn"
      >
        {isPublic ? 'Watch the tour' : 'Begin the tour'}
        <Rocket className="h-5 w-5 group-hover:translate-x-0.5 transition" />
      </button>
    </div>
  );
}

function EndOverlay({ isPublic, onReplay }) {
  if (!isPublic) {
    return (
      <div className="text-center max-w-xl" data-testid="intro-end-overlay">
        <div className="text-xs uppercase tracking-[0.4em] text-white/40 mb-6">Ready</div>
        <h2 className="text-4xl sm:text-6xl font-light leading-tight mb-4">Let&apos;s set up your workspace.</h2>
        <p className="text-white/50">Loading your setup wizard…</p>
      </div>
    );
  }
  // Public tour end — funnel the visitor toward signup.
  return (
    <div className="text-center max-w-2xl" data-testid="intro-end-overlay">
      <div className="text-xs uppercase tracking-[0.4em] text-white/40 mb-6">That&apos;s Konnect.</div>
      <h2 className="text-4xl sm:text-6xl font-light leading-[1.05] mb-3">
        Ready to run your business on one screen?
      </h2>
      <p className="text-white/60 mb-10 max-w-lg mx-auto">
        Start a free 7-day trial. No card required. Cancel anytime, keep everything you built.
      </p>
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <a
          href="/signup"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black hover:bg-white/90 transition font-medium"
          data-testid="intro-signup-cta"
        >
          Start free trial <Rocket className="h-5 w-5" />
        </a>
        <a
          href="/login"
          className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/15 hover:bg-white/5 transition text-white/80"
          data-testid="intro-login-cta"
        >
          I already have an account
        </a>
        <button
          onClick={onReplay}
          className="inline-flex items-center gap-2 px-5 py-4 text-white/60 hover:text-white/90 transition text-sm"
          data-testid="intro-replay-cta"
        >
          Watch again
        </button>
      </div>
    </div>
  );
}

// ─── shared scene shell ──────────────────────────────────────────────────────
function SceneStage({ chapter, scene, localProgress }) {
  // Ken-Burns style zoom-in: scale 1.02 → 1.15 across chapter duration
  const scale = 1.02 + localProgress * 0.13;
  const translateY = (1 - localProgress) * 12;
  return (
    <div
      key={chapter?.id}
      className="w-full h-full flex items-center justify-center"
      style={{ transform: `translateY(${translateY}px)`, opacity: Math.min(1, 0.15 + localProgress * 4) }}
    >
      <div
        className="relative w-full max-w-5xl aspect-video rounded-3xl overflow-hidden border border-white/10 bg-black/40 backdrop-blur-xl shadow-[0_60px_120px_-40px_rgba(0,0,0,0.8)]"
        style={{ transform: `scale(${scale})`, transition: 'transform 400ms linear' }}
      >
        {scene.render(localProgress)}
        {/* Corner label */}
        <div className="absolute top-6 left-6 flex items-center gap-3 text-white/70">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ backgroundColor: `${scene.accent}22`, color: scene.accent }}>
            <scene.Icon className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Module</div>
            <div className="text-sm font-medium">{scene.label}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── individual scenes ───────────────────────────────────────────────────────
function WelcomeScene() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="text-center">
        <img src="/konnect-logo-white.png" alt="" className="h-20 w-auto mx-auto mb-6 opacity-90" />
        <div className="text-6xl font-light tracking-tight text-white">KONNECT</div>
        <div className="text-white/40 mt-3 text-xs uppercase tracking-[0.5em]">One System. Every Function.</div>
      </div>
      {/* animated dots */}
      <FloatingDots color="#22d3ee" />
    </div>
  );
}

function MeetScene() {
  const tiles = ['Anna', 'Marco', 'Priya', 'Yuki', 'Luis', 'You'];
  return (
    <div className="absolute inset-0 p-8 grid grid-cols-3 gap-3">
      {tiles.map((n, i) => (
        <div key={n} className="rounded-2xl bg-gradient-to-br from-white/10 to-white/[0.02] border border-white/10 flex items-center justify-center relative overflow-hidden">
          <div className="text-white/70 text-lg font-medium">{n}</div>
          <div className="absolute bottom-2 left-2 flex items-center gap-1 text-[10px] text-white/40">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />live
          </div>
          <div className="absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-black/40 to-transparent" />
          {i === 5 && <div className="absolute inset-0 border-2 border-violet-400/60 rounded-2xl" />}
        </div>
      ))}
      <div className="col-span-3 -mt-1 flex items-center justify-between text-[11px] text-white/40 pt-1">
        <span>KonnectMeet · HD · encrypted</span>
        <span>Auto-transcribing…</span>
      </div>
    </div>
  );
}

function PromptScene({ progress }) {
  const chars = 'Design a launch campaign for our new SKU across email, IG, and TikTok. Include hooks.'.slice(0, Math.floor(progress * 92));
  return (
    <div className="absolute inset-0 p-10 flex flex-col justify-center">
      <div className="text-xs uppercase tracking-[0.3em] text-pink-300/60 mb-3">PowerPrompt</div>
      <div className="rounded-2xl border border-white/10 bg-black/50 p-5 text-white/90 text-lg font-light min-h-[80px]">
        {chars}<span className="inline-block w-2 h-5 bg-pink-400/80 ml-1 animate-pulse align-middle" />
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        {['GPT · reasoning', 'Claude · copy', 'Gemini · research', 'Nano Banana · art', 'Suno · music'].map((m, i) => (
          <div key={m}
               className="px-3 py-1.5 rounded-full text-xs border border-white/10 bg-white/5 text-white/70"
               style={{ opacity: progress > (i * 0.15 + 0.15) ? 1 : 0.25, transition: 'opacity 500ms' }}>
            {m}
          </div>
        ))}
      </div>
    </div>
  );
}

function VendingScene({ progress }) {
  const pins = [
    { x: 22, y: 62 }, { x: 41, y: 42 }, { x: 58, y: 70 },
    { x: 71, y: 32 }, { x: 79, y: 58 }, { x: 34, y: 78 },
  ];
  return (
    <div className="absolute inset-0">
      {/* faux map lines */}
      <svg viewBox="0 0 100 60" className="absolute inset-0 w-full h-full opacity-40">
        <defs>
          <pattern id="grid" width="4" height="4" patternUnits="userSpaceOnUse">
            <path d="M 4 0 L 0 0 0 4" fill="none" stroke="#facc15" strokeOpacity=".18" strokeWidth=".2" />
          </pattern>
        </defs>
        <rect width="100" height="60" fill="url(#grid)" />
        <path d="M 5 45 Q 30 20 55 40 T 95 30" stroke="#facc15" strokeWidth=".4" fill="none" strokeDasharray="1,1" />
      </svg>
      {pins.map((p, i) => (
        <div key={i}
             className="absolute -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-amber-300"
             style={{
               left: `${p.x}%`, top: `${p.y}%`,
               background: '#facc15',
               opacity: progress > (i * 0.14) ? 1 : 0.15,
               boxShadow: '0 0 22px rgba(250, 204, 21, 0.6)',
               transition: 'opacity 600ms',
             }}>
          <div className="absolute inset-0 rounded-full animate-ping bg-amber-300/40" />
        </div>
      ))}
      <div className="absolute bottom-6 left-8 right-8 grid grid-cols-3 gap-2 text-[11px] text-white/70">
        <StatCard label="Fleet online" value={`${Math.floor(48 + progress * 4)}/52`} tone="#facc15" />
        <StatCard label="Restock alerts" value={`${Math.floor(2 + progress * 2)}`} tone="#facc15" />
        <StatCard label="Route saved" value={`${Math.floor(38 + progress * 10)}%`} tone="#facc15" />
      </div>
    </div>
  );
}

function KoolKonnectScene({ progress }) {
  const boards = ['STORY', 'SHOT', 'VOICE', 'MUSIC', 'CUT'];
  return (
    <div className="absolute inset-0 p-8 flex flex-col justify-center">
      <div className="flex gap-3 mb-4">
        {boards.map((b, i) => (
          <div key={b}
               className="flex-1 h-24 rounded-xl border border-emerald-400/30 bg-emerald-400/5 flex items-center justify-center text-emerald-300 text-xs tracking-widest"
               style={{ opacity: progress > i * 0.18 ? 1 : 0.2, transform: `translateY(${progress > i * 0.18 ? 0 : 8}px)`, transition: 'all 600ms' }}>
            {b}
          </div>
        ))}
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full bg-emerald-400" style={{ width: `${progress * 100}%`, transition: 'width 300ms' }} />
      </div>
      <div className="mt-4 flex justify-between text-[11px] text-white/50">
        <span>Storyboard → Assembly · Kaharian Gateway</span>
        <span>Render {(progress * 100).toFixed(0)}%</span>
      </div>
    </div>
  );
}

function BookingScene({ progress }) {
  const slots = Array.from({ length: 6 * 4 }, (_, i) => i);
  return (
    <div className="absolute inset-0 p-8 grid grid-cols-6 gap-2 content-center">
      {slots.map((i) => {
        const filled = i % 3 === 0 || i % 7 === 0;
        const active = progress > (i / slots.length);
        return (
          <div key={i}
               className={`h-10 rounded-lg border ${filled ? 'bg-blue-400/30 border-blue-300/40' : 'bg-white/5 border-white/10'}`}
               style={{ opacity: active ? 1 : 0.2, transition: 'opacity 500ms' }} />
        );
      })}
      <div className="col-span-6 mt-3 flex justify-between text-[11px] text-white/60">
        <span>Booking Studio · services · resources · waitlist</span>
        <span>{Math.floor(progress * 32)} bookings today</span>
      </div>
    </div>
  );
}

function OutroScene({ progress }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="text-center">
        <div className="text-white/50 text-xs uppercase tracking-[0.4em] mb-4">Your workspace is 1 step away</div>
        <div className="text-5xl sm:text-6xl font-light leading-[1.02] text-white">
          Ready to launch<span className="inline-block w-2 h-10 bg-coral-400 align-middle ml-2"
                                 style={{ background: '#f87171', opacity: progress > 0.4 ? 1 : 0 }} />
        </div>
      </div>
    </div>
  );
}

// ─── mini helpers ────────────────────────────────────────────────────────────
function StatCard({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 backdrop-blur px-3 py-2">
      <div className="text-white/40 text-[10px] uppercase tracking-widest">{label}</div>
      <div className="text-lg font-medium" style={{ color: tone }}>{value}</div>
    </div>
  );
}

function FloatingDots({ color }) {
  const dots = Array.from({ length: 18 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {dots.map((_, i) => (
        <div key={i}
             className="absolute h-1 w-1 rounded-full"
             style={{
               left: `${(i * 13) % 100}%`,
               top: `${(i * 29) % 100}%`,
               background: color,
               opacity: 0.15 + (i % 5) * 0.1,
               animation: `float ${8 + (i % 6)}s ease-in-out ${i * 0.2}s infinite alternate`,
             }} />
      ))}
      <style>{`@keyframes float { from { transform: translateY(0) } to { transform: translateY(-16px) } }`}</style>
    </div>
  );
}
